import React from "react";
import {
  FaBell,
  FaCheckCircle,
  FaCoins,
  FaCreditCard,
  FaFileInvoice,
  FaLock,
} from "react-icons/fa";

import {
  FALLBACK_IMAGE,
  canGenerateInvoice,
  canPayInInstallments,
  canShowPaymentButtons,
  formatDisplayDate,
  formatEUR,
  getArticleMessage,
  getArticleName,
  getStatusMeta,
} from "./../../../../dashboard/pages/user/Reglements/utils/reglementsUtils";

export default function ReglementCard({
  reglement,
  payingId,
  notifyingId,
  isAlreadyNotified,
  onPay,
  onInstallments,
  onNotify,
  onInvoice,
}) {
  const statusMeta = getStatusMeta(reglement?.statut_reglement);
  const name = getArticleName(reglement);
  const message = getArticleMessage(reglement);

  const isPaymentDisabled = reglement?.autorisation_achat === false;
  const showPaymentButtons = canShowPaymentButtons(reglement);
  const showInstallments = canPayInInstallments(reglement);
  const alreadyNotified = isAlreadyNotified?.(reglement.id);
  const showInvoice = canGenerateInvoice(reglement);

  return (
    <article className="group overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-lg shadow-slate-200/60 transition dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/10">
      <div className="h-1 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4">
        <div className="flex gap-3">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
            <img
              src={reglement.photoUrl || FALLBACK_IMAGE}
              alt={name}
              className="h-full w-full object-contain p-1"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  {reglement.saison}
                </p>

                <h3 className="mt-1 line-clamp-2 text-base font-bold leading-6 text-slate-950 dark:text-white">
                  {reglement.quantite} x {name}
                </h3>
              </div>
            </div>

            <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
              Achat du {formatDisplayDate(reglement.date_achat)}
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 ${statusMeta.badge}`}
              >
                <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
                {statusMeta.label}
              </span>
            </div>
          </div>
        </div>

        {message && (
          <p className="mt-4 line-clamp-3 rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm italic leading-6 text-slate-600 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
            {message}
          </p>
        )}

        <div className="mt-4 rounded-2xl bg-slate-950 px-4 py-3 text-white dark:bg-white dark:text-slate-950">
          <p className="text-xs font-bold uppercase tracking-[0.16em] opacity-70">
            Montant
          </p>

          <p className="mt-1 text-2xl font-bold">
            {formatEUR(reglement.prix_total)}
          </p>
        </div>

        {showPaymentButtons && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60">
            {isPaymentDisabled && (
              <div className="mb-3 inline-flex items-center gap-2 rounded-2xl bg-red-50 px-3 py-2 text-xs font-bold text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20">
                <FaLock />
                Paiement désactivé par le bureau
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => onPay(reglement.id)}
                disabled={isPaymentDisabled || payingId === reglement.id}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 text-sm font-bold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:bg-indigo-800 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaCreditCard />
                {payingId === reglement.id
                  ? "Redirection..."
                  : "Payer en ligne"}
              </button>

              {showInstallments && (
                <button
                  type="button"
                  onClick={() => onInstallments(reglement.id)}
                  disabled={isPaymentDisabled}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900"
                >
                  <FaCoins />
                  Payer en plusieurs fois
                </button>
              )}
            </div>
          </div>
        )}

        {reglement.statut_reglement === "Réglé" && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {showInvoice && (
              <button
                type="button"
                onClick={() => onInvoice?.(reglement)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 text-sm font-bold text-white transition hover:bg-indigo-800"
              >
                <FaFileInvoice />
                Facture PDF
              </button>
            )}

            {alreadyNotified ? (
              <span className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-50 px-3 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                <FaCheckCircle />
                Admin prévenu
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onNotify(reglement)}
                disabled={notifyingId === reglement.id}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaBell />
                {notifyingId === reglement.id ? "Envoi..." : "Prévenir l’admin"}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}